import { CREW, CREW_TITLES, DAMAGE, LANTERN_BEARINGS, LANTERN_COLOURS, LANTERN_INTERACTIONS, LANTERN_RULES, LANTERN_SITES, MODE_LAUNCH, PISTOL, ROPES, SAILS, SHIP, breachSeverity, getIsland, ropeCut, sailHoleCount, sailShredded, shipDrive, type CrewChoice, type Equipment, type IslandId, type LanternInteractionId, type LanternState, type QuestKind, type SeaStatus, type SaleMsg, type ToastKind } from "@tiny-seas/shared";
import { HOLE_NAMES } from "../repairs.ts";
import { ONBOARDING_STEPS, settings, type OnboardingStep } from "../settings.ts";
import { GRAPHICS_PRESETS, GRAPHICS_PRESET_IDS, graphicsPreset } from "../scene/graphics.ts";
import { Changelog, currentVersion } from "./changelog.ts";
import { byId, h, setStyle, setText, toggle } from "./dom.ts";
import type { PhaseChip, PhaseSplash, RoyaleResults, SpectatorView } from "../royale-status.ts";
import { WorldMap, lanternMapSvg, questMapSvg, type MapMarker } from "./maps.ts";

export interface CrewEntry {
  id: string;
  name: string;
  connected: boolean;
  bot: boolean;
  self: boolean;
  dead: boolean;
  station: string;
  cannonLoaded: boolean;
  mode: number;
}

export interface CrewView {
  slot: number;
  name: string;
  color: string;
  members: number;
  humans: number;
  memberNames: readonly string[];
  gold: number;
}

export interface HelmInfo {
  speed: number;
  heading: number;
  sail: number;
}

/** The player's ship as the status panel reads it (`ShipState`). */
export interface ShipView {
  leaks: number;
  breaches: number;
  flooding: number;
  ropes: number;
  sailHoles: number;
  wheelHp: number;
  cannons: { readonly length: number; at(index: number): { wrecked: boolean } | undefined };
}

const capitalise = (text: string): string => text[0]!.toUpperCase() + text.slice(1);

/** Damage readout lines, worst first; empty when the ship is whole. */
export function damageReport(ship: ShipView): string[] {
  const items: string[] = [];
  if (ship.wheelHp <= 0) items.push("Helm smashed");
  else if (ship.wheelHp < DAMAGE.wheelHp) items.push("Helm damaged");
  let worst = 0;
  for (let i = 0; i < SHIP.leakSlots; i++) worst = Math.max(worst, breachSeverity(ship.leaks, ship.breaches, i));
  if (worst > 0) items.push(`Worst hole: ${HOLE_NAMES[worst]}`);
  let guns = 0;
  for (let i = 0; i < ship.cannons.length; i++) if (ship.cannons.at(i)?.wrecked) guns++;
  if (guns) items.push(`${guns} gun${guns === 1 ? "" : "s"} dismounted`);
  let cut = 0;
  for (let i = 0; i < ROPES.length; i++) if (ropeCut(ship.ropes, i)) cut++;
  if (cut) items.push(`${cut} line${cut === 1 ? "" : "s"} cut`);
  SAILS.forEach((sail, i) => {
    if (sailShredded(ship.sailHoles, i)) items.push(`${capitalise(sail.label)} torn away`);
    else {
      const holes = sailHoleCount(ship.sailHoles, i);
      if (holes) items.push(`${capitalise(sail.label)}: ${holes} hole${holes === 1 ? "" : "s"}`);
    }
  });
  return items;
}

export interface VoteView {
  initiator: string;
  yes: number;
  needed: number;
  endsIn: number;
  iVoted: boolean;
}

export interface MenuHandlers {
  resume(): void;
  recover(): void;
  abandon(): void;
  leave(): void;
  chooseCrew(choice: CrewChoice): void;
  releaseForDialog(): void;
  bot(add: boolean): void;
  takeQuest(kind: QuestKind): void;
  online(): void;
}

export interface OnlineView {
  population: SeaStatus;
  crews: readonly { slot: number; name: string; color: string; gold: number; quests: number }[];
  players: readonly { id: string; name: string; crewName: string; bot: boolean; connected: boolean }[];
}

const ONBOARDING_TEXT: Record<OnboardingStep, string> = {
  move: "Look around and walk (mouse + WASD)",
  board: "Get aboard the ship",
  wheel: "Take the wheel (E) and raise the anchor",
  quest: "Accept a quest at the quest board",
  dig: "Dig up the treasure at the X",
  sell: "Sell the chest to the merchant",
};

const SAIL_NAMES = ["Stowed", "⅓ sail", "⅔ sail", "Full sail"];
const TOAST_ICON: Record<ToastKind, string> = { info: "ℹ", good: "✔", warn: "⚠", bad: "✖" };

function compass(rad: number): string {
  const deg = ((rad * 180) / Math.PI + 360) % 360;
  const names = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return `${names[Math.round(deg / 45) % 8]} ${Math.round(deg)}°`;
}

const TEMPLATE = `
<div class="scope-mask" data-ref="scopeMask" aria-hidden="true" hidden><div class="scope-lens"><span data-ref="scopeLabel">4x</span></div></div>
<div class="crosshair" aria-hidden="true"></div>
<div class="melee-marker" data-ref="meleeMarker" aria-hidden="true" hidden></div>
<div class="hurt-vignette" data-ref="hurtVignette" aria-hidden="true" hidden></div>
<section class="panel equipment" aria-label="Equipment">
  <div class="equipment-slots">
    <span data-ref="equipSword" class="equipment-slot">1 Cutlass</span>
    <span data-ref="equipShovel" class="equipment-slot selected">2 Shovel</span>
    <span data-ref="equipSpyglass" class="equipment-slot">3 Spyglass</span>
    <span data-ref="equipPistol" class="equipment-slot">4 Revolver</span>
  </div>
  <div class="hint" data-ref="equipmentHint">Left click: Dig on land</div>
  <div class="hint" data-ref="pistolAmmo" hidden></div>
  <div class="hint" data-ref="pistolStatus" hidden></div>
  <div class="hint" data-ref="locomotion"></div>
</section>
<div class="prompt" data-ref="prompt" hidden><span class="ring" data-ref="ring"></span><span data-ref="promptText"></span></div>

<section class="panel crew" aria-label="Crew">
  <div class="room-row"><button class="small" data-ref="onlineBtn" type="button">Players</button>
    <button class="small" data-ref="copy" type="button">Copy crew invite</button></div>
  <div data-ref="population" class="hint"></div>
  <div data-ref="populationBreakdown" class="hint"></div>
  <div class="label"><span data-ref="ownSwatch" aria-hidden="true" style="display:inline-block;width:0.8em;height:0.8em;margin-right:0.35em"></span><span data-ref="ownCrew">Choose a crew</span></div>
  <ul data-ref="crewList" class="crew-list"></ul>
</section>

<section class="panel loot" aria-label="Crew gold">
  <div class="gold"><span aria-hidden="true">🪙</span> <strong data-ref="gold">0</strong> gold</div>
  <div class="title" data-ref="title"></div>
</section>

<section class="panel quest" aria-label="Current objective">
  <div class="label">Objective</div>
  <div data-ref="objective" class="objective"></div>
  <div data-ref="questHint" class="hint" hidden></div>
  <div data-ref="islandObjective" class="island-objective hint" hidden></div>
  <div class="hint keys">M: map · P: players · Esc: menu</div>
</section>

<section class="panel vitals" aria-label="Your status">
  <div class="bar-row"><span class="label">♥ Health</span><div class="bar"><div data-ref="health" class="fill health"></div></div><span data-ref="healthNum" class="num"></span></div>
  <div class="bar-row" data-ref="breathRow" hidden><span class="label">◯ Breath</span><div class="bar"><div data-ref="breath" class="fill breath"></div></div><span data-ref="breathNum" class="num"></span></div>
  <div data-ref="carry" class="carry" hidden>📦 Carrying treasure — Q to drop</div>
</section>

<section class="panel shipstat" aria-label="Ship status" data-ref="shipPanel">
  <div class="label">Ship</div>
  <div class="bar-row"><span class="label">Hull</span><div class="bar"><div data-ref="hull" class="fill hull"></div></div><span data-ref="hullNum" class="num"></span></div>
  <div class="bar-row"><span class="label">Water</span><div class="bar"><div data-ref="flood" class="fill flood"></div></div><span data-ref="floodNum" class="num"></span></div>
  <div data-ref="damage" hidden>
    <div class="bar-row"><span class="label">Drive</span><div class="bar"><div data-ref="drive" class="fill drive"></div></div><span data-ref="driveNum" class="num"></span></div>
    <div data-ref="damageList" class="hint damage-list"></div>
  </div>
  <div data-ref="helm" class="helm" hidden>
    <div><span class="label">Speed</span> <strong data-ref="speed"></strong></div>
    <div><span class="label">Heading</span> <strong data-ref="heading"></strong></div>
    <div><span class="label">Sail</span> <strong data-ref="sail"></strong></div>
    <div class="hint">A/D steer · W/S sail · E leave wheel</div>
  </div>
  <div data-ref="anchorState" class="hint"></div>
  <div data-ref="supplies" class="hint"></div>
  <div data-ref="cannonReload" class="hint" hidden></div>
</section>

<div class="toasts" data-ref="toasts" role="status" aria-live="polite"></div>
<div class="banner warn" data-ref="boundary" hidden>⚠ You are nearing the edge of the Tiny Seas. Turn back!</div>
<div class="banner" data-ref="vote" hidden></div>
<div class="banner" data-ref="recover" hidden></div>
<div class="banner bad" data-ref="conn" hidden></div>
<div class="banner warn" data-ref="royaleRing" hidden></div>
<div class="phase-chip" data-ref="phaseChip" hidden><span class="phase-tag" data-ref="phaseTag"></span><span data-ref="phaseDetail"></span><span class="phase-role" data-ref="phaseRole"></span></div>
<div class="phase-splash" data-ref="phaseSplash" hidden><strong data-ref="phaseSplashTitle"></strong><span data-ref="phaseSplashText"></span></div>
<div class="phase-results" data-ref="royaleResults" hidden><h2 data-ref="resultsTitle"></h2><p data-ref="resultsReason"></p><p class="hint" data-ref="resultsNext"></p></div>
<div class="spectate-card" data-ref="spectateCard" hidden><strong data-ref="spectateTarget"></strong><p class="hint" data-ref="spectateStatus"></p><div class="row gap"><button type="button" class="primary" data-ref="spectateJoin">Join an afloat crew (J)</button><button type="button" data-ref="spectateWait">Start a new crew next round</button></div></div>
<div class="treasure-pointer" data-ref="treasurePointer" hidden><span class="treasure-arrow" data-ref="treasureArrow">▲</span><span data-ref="treasureLabel"></span></div>

<div class="overlay dim" data-ref="death" hidden><div class="card center"><h2>Knocked out!</h2><p data-ref="deathText"></p></div></div>
<div class="overlay dim sinking" data-ref="sinking" hidden><div class="card center"><h2>⚓ The ship is sinking!</h2><p data-ref="sinkText"></p></div></div>

<section class="panel onboarding" data-ref="onboarding" aria-label="Getting started">
  <div class="row"><strong>First voyage</strong><button class="small" type="button" data-ref="hideOnboarding" aria-label="Hide checklist">Hide</button></div>
  <ol data-ref="onboardingList"></ol>
</section>

<div class="overlay" data-ref="mapOverlay" hidden role="dialog" aria-modal="true" aria-label="Map">
  <div class="card map-card">
    <div class="map-world" data-ref="mapWorld"></div>
    <aside class="map-quest">
      <h3 data-ref="mapQuestTitle">No active quest</h3>
      <div data-ref="mapQuest"></div>
      <p class="hint" data-ref="mapQuestHint">Visit any outpost quest board to choose a treasure quest.</p>
      <section class="map-journal-entry" aria-label="Island exploration">
        <h3>Lantern Isle</h3>
        <p class="hint">Follow the island footpaths and uncover the keeper's story. Your crew shares discoveries and collected notes.</p>
        <p class="hint" data-ref="journalSummary"></p>
        <button type="button" data-ref="journalOpen">Open exploration journal</button>
      </section>
      <p class="hint">Press M or Esc to close.</p>
      <button type="button" data-ref="mapClose">Close map &amp; return</button>
    </aside>
  </div>
</div>

<dialog class="island-notes" data-ref="islandDialog" aria-labelledby="lantern-notes-title">
  <header class="island-notes-header">
    <p class="label" data-ref="islandCaption">Lantern Isle · crew exploration</p>
    <h2 id="lantern-notes-title" data-ref="islandTitle">Exploration journal</h2>
  </header>
  <div class="island-notes-content" tabindex="0" data-ref="islandScroll">
    <article data-ref="islandNoteView" hidden>
      <div class="island-note-text" data-ref="islandNoteText"></div>
      <p class="hint">This note is kept in your crew's exploration journal for this voyage.</p>
    </article>
    <section data-ref="islandJournalView">
      <p class="hint">A shared record for your crew in this sea. Exploring does not replace your quest-board treasure hunt.</p>
      <div class="journal-layout">
        <div class="journal-chart" data-ref="journalChart"></div>
        <section aria-label="Discovered places"><h3>Discovered places</h3><ul class="journal-sites" data-ref="journalSites"></ul></section>
      </div>
      <section aria-label="Collected clues"><h3>Collected notes</h3><div class="journal-clues" data-ref="journalClues"></div></section>
      <p class="journal-signal" data-ref="journalSignal" aria-live="polite"></p>
    </section>
  </div>
  <footer><p class="hint">Esc closes · gameplay controls are released while reading.</p><button type="button" data-ref="islandClose">Close journal</button></footer>
</dialog>

<div class="overlay" data-ref="sale" hidden role="dialog" aria-label="Treasure sold">
  <div class="card center sale-card">
    <h2>Treasure sold!</h2>
    <p data-ref="saleChest"></p>
    <p class="big">+<span data-ref="saleEarned"></span> gold</p>
    <p>Crew total: <strong data-ref="saleTotal"></strong> gold · Quests completed: <strong data-ref="saleQuests"></strong></p>
    <p>Crew title: <strong data-ref="saleTitle"></strong> <span data-ref="saleNew" class="badge" hidden>New title!</span></p>
    <button type="button" data-ref="saleClose">Onward!</button>
  </div>
</div>

<div class="overlay" data-ref="questDialog" hidden role="dialog" aria-label="Quest board">
  <div class="card center">
    <h2 data-ref="qdTitle"></h2>
    <div data-ref="qdMap"></div>
    <p class="hint" data-ref="qdHint"></p>
    <div class="row gap"><button type="button" data-ref="qdAbandon" class="danger">Abandon quest…</button><button type="button" data-ref="qdClose">Close</button></div>
  </div>
</div>

<div class="overlay dim" data-ref="online" hidden role="dialog" aria-modal="true" aria-label="Pirates at sea">
  <div class="card online-card">
    <h2>Pirates at sea</h2>
    <p data-ref="onlinePopulation"></p><p data-ref="onlineBreakdown" class="hint"></p>
    <h3>Leaderboard</h3><ol data-ref="leaderboard" class="online-list"></ol>
    <h3>Sailors</h3><ul data-ref="onlineList" class="online-list"></ul>
    <button type="button" data-ref="onlineClose">Close</button>
  </div>
</div>
<div class="overlay dim" data-ref="board" hidden role="dialog" aria-modal="true" aria-label="Choose a treasure quest">
  <div class="card center">
    <h2>Quest board</h2>
    <div class="menu-buttons">
      <button type="button" data-ref="takeMap">Treasure map</button>
      <p class="hint">One island · 500–3000 gold</p>
      <button type="button" data-ref="takeHunt">Treasure hunt</button>
      <p class="hint">Three islands · 3000 gold</p>
      <button type="button" data-ref="boardClose">Close</button>
    </div>
  </div>
</div>

<div class="overlay dim" data-ref="crewPicker" hidden role="dialog" aria-modal="true" aria-label="Choose a crew">
  <div class="card crew-picker">
    <h2>Choose your crew</h2>
    <p class="hint">Up to four sailors per crew, including bots. Each crew has its own ship, quests and gold.</p>
    <div data-ref="crewChoices" class="crew-choices"></div>
    <div class="row gap"><button type="button" data-ref="newCrew" class="primary">Start a new crew</button><button type="button" data-ref="crewClose">Close</button></div>
  </div>
</div>

<div class="overlay" data-ref="confirm" hidden role="alertdialog" aria-label="Confirm">
  <div class="card center"><p data-ref="confirmText"></p>
  <div class="row gap"><button type="button" data-ref="confirmYes" class="danger">Yes</button><button type="button" data-ref="confirmNo">No</button></div></div>
</div>

<div class="overlay dim" data-ref="pause" hidden role="dialog" aria-label="Menu">
  <div class="card menu">
    <h2>Tiny Seas</h2>
    <p class="hint">Version ${currentVersion}</p>
    <p data-ref="pauseNote" class="hint"></p>
    <div class="menu-buttons">
      <button type="button" data-ref="resume" class="primary">Resume</button>
      <button type="button" data-ref="recoverBtn">Recover (I'm stuck)</button>
      <button type="button" data-ref="abandonBtn">Abandon quest…</button>
      <button type="button" data-ref="changeCrew">Change crew…</button>
      <button type="button" data-ref="addBot">Add bot crewmate</button>
      <button type="button" data-ref="removeBot">Dismiss bot crewmate</button>
      <button type="button" data-ref="onlineMenuBtn">Players</button>
      <button type="button" data-ref="showGuide">Show first-voyage checklist</button>
      <button type="button" data-ref="changelog">What’s new</button>
      <button type="button" data-ref="leaveBtn" class="danger">Leave sea</button>
    </div>
    <details><summary>Settings</summary>
      <label>Graphics quality
        <select data-ref="graphicsPreset" aria-describedby="graphics-quality-help">
          ${GRAPHICS_PRESET_IDS.map(id => `<option value="${id}">${GRAPHICS_PRESETS[id].label}</option>`).join("")}
        </select>
      </label>
      <p id="graphics-quality-help" class="hint">Low turns off the ocean's reflections and refractions (it reflects the sky instead) for the fastest frames. Balanced caps rendering at 1× pixel density and lowers capture/shadow resolution and sample counts; Ultra allows up to 2× pixel density. Changes apply live and are saved in this browser.</p>
      <label>Contact shading
        <select data-ref="contactShadowSamples" aria-describedby="contact-shadow-help">
          <option value="48">48 samples · Full (default)</option>
          <option value="24">24 samples · Faster comparison</option>
        </select>
      </label>
      <p id="contact-shadow-help" class="hint">Independent of graphics quality. Both retain full-resolution contact shading; 24 samples reduce shading work but may add fine noise.</p>
      <label>Mouse sensitivity <input type="range" min="0.2" max="3" step="0.1" data-ref="sens"></label>
      <label><input type="checkbox" data-ref="invert"> Invert mouse Y</label>
      <label>Master volume <input type="range" min="0" max="1" step="0.05" data-ref="vMaster"></label>
      <label>Effects volume <input type="range" min="0" max="1" step="0.05" data-ref="vFx"></label>
      <label>Ambience volume <input type="range" min="0" max="1" step="0.05" data-ref="vAmb"></label>
      <label><input type="checkbox" data-ref="mute"> Mute all sound</label>
    </details>
    <details><summary>Controls</summary>
      <table class="controls">
        <tr><td>WASD</td><td>Walk / swim · A/D steer, W/S sail at the wheel · walk or swim into a ladder to grab it, then W climbs toward your view (look well down to descend), S the other way, release to hang</td></tr>
        <tr><td>Mouse</td><td>Look · aim a cannon before climbing in</td></tr>
        <tr><td>Space</td><td>Jump / swim up · jump off a ladder</td></tr>
        <tr><td>Shift</td><td>Hold to sprint forwards</td></tr>
        <tr><td>C</td><td>Hold to crouch · press after sprinting to slide · dive in water</td></tr>
        <tr><td>E</td><td>Interact · hold to repair · take or return a bucket at the rack · restock supplies · leave wheel · climb out of cannon, then leave station</td></tr>
        <tr><td>F</td><td>Climb into an operated cannon; aim locks until you climb out</td></tr>
        <tr><td>1 / 2 / 3 / 4</td><td>Equip cutlass / shovel / spyglass / revolver</td></tr>
        <tr><td>Left mouse</td><td>Slash / click late after a hit to chain · dig with shovel · fire revolver or cannon · launch yourself when loaded · with a bucket: fill it in flood water, throw it at the rail</td></tr>
        <tr><td>Right mouse</td><td>Hold cutlass guard · raise just before impact to parry · hold spyglass for 4× view · aim revolver</td></tr>
        <tr><td>R</td><td>Reload / top up revolver cylinder</td></tr>
        <tr><td>V</td><td>Toggle first person / third-person chase camera</td></tr>
        <tr><td>Q</td><td>Drop carried chest</td></tr>
        <tr><td>M</td><td>Map</td></tr>
        <tr><td>P</td><td>Players and leaderboard</td></tr>
        <tr><td>Y / N</td><td>Vote on abandoning a quest</td></tr>
        <tr><td>Esc</td><td>Release mouse, open this menu</td></tr>
      </table>
    </details>
  </div>
</div>
`;

export class Hud {
  readonly root: HTMLElement;
  private readonly r: Record<string, HTMLElement> = {};
  readonly worldMap: WorldMap;
  private autoPick = true;
  private questKey = "";
  private crewKey = "";
  private saleTimer = 0;
  private meleeTimer = 0;
  private splashTimer = 0;
  private hurtTimer = 0;
  /** Damage the readout last showed; wheelHp -1 forces the first build. */
  private readonly damageShown = { leaks: 0, breaches: 0, ropes: 0, sailHoles: 0, wheelHp: -1, wrecked: 0, drive: 0 };
  onboardingVisible = !settings.value.onboardingHidden;
  private confirmCb: (() => void) | null = null;
  private crewsKey = "";
  private ownSlot = -1;
  private readonly handlers: MenuHandlers;
  private onlineKey = "";
  private dialogFocus: HTMLElement | null = null;
  private readonly changelog: Changelog;
  private readonly islandDialog: HTMLDialogElement;
  private readonly lanternSnapshot = { crewSlot: -2, discoveries: -1, clues: -1, solved: false, bearing: -1, colour: -1, revealed: false, cargoTaken: false };
  private lanternDiscoveries = 0;
  private islandReturnToJournal = false;
  private islandFocus: HTMLElement | null = null;

  constructor(parent: HTMLElement, handlers: MenuHandlers, royale = false) {
    this.handlers = handlers;
    this.worldMap = new WorldMap({ royale });
    this.root = h("div", { class: "hud", "data-testid": "hud" });
    this.root.innerHTML = TEMPLATE;
    parent.append(this.root);
    this.changelog = new Changelog(this.root);
    for (const e of this.root.querySelectorAll<HTMLElement>("[data-ref]")) this.r[e.dataset.ref!] = e;
    this.islandDialog = byId(this.root, "islandDialog", HTMLDialogElement);
    this.ref("mapWorld").append(this.worldMap.svg);
    if (royale) {
      setText(this.ref("mapQuestTitle"), "Battle royale");
      setText(this.ref("mapQuestHint"), "Red ring: safe water. ★ treasure · ● loot barrels.");
      const journal = this.root.querySelector<HTMLElement>(".map-journal-entry");
      if (journal) toggle(journal, false);
    }
    this.ref("journalOpen").addEventListener("click", () => this.openIslandJournal());
    this.ref("mapClose").addEventListener("click", () => { this.setMapOpen(false); handlers.resume(); });
    this.ref("islandClose").addEventListener("click", () => {
      if (this.islandReturnToJournal) { this.showJournalPage(); return; }
      this.closeIslandNotes();
      if (!this.mapOpen) handlers.resume();
    });
    this.ref("islandDialog").addEventListener("cancel", (event) => {
      event.preventDefault();
      this.closeIslandNotes();
    });
    this.ref("copy").addEventListener("click", () => void this.copyLink());
    this.ref("onlineBtn").addEventListener("click", () => handlers.online());
    this.ref("onlineMenuBtn").addEventListener("click", () => handlers.online());
    this.ref("onlineClose").addEventListener("click", () => this.closeOnline());
    this.ref("boardClose").addEventListener("click", () => this.closeBoard());
    this.ref("takeMap").addEventListener("click", () => { handlers.takeQuest("map"); this.closeBoard(); });
    this.ref("takeHunt").addEventListener("click", () => { handlers.takeQuest("hunt"); this.closeBoard(); });
    this.ref("addBot").addEventListener("click", () => handlers.bot(true));
    this.ref("removeBot").addEventListener("click", () => handlers.bot(false));
    this.ref("hideOnboarding").addEventListener("click", () => this.setOnboardingVisible(false));
    this.ref("showGuide").addEventListener("click", () => this.setOnboardingVisible(true));
    this.ref("changelog").addEventListener("click", () => {
      this.changelog.open();
      handlers.releaseForDialog();
    });
    this.ref("resume").addEventListener("click", () => handlers.resume());
    this.ref("recoverBtn").addEventListener("click", () => handlers.recover());
    this.ref("abandonBtn").addEventListener("click", () => handlers.abandon());
    this.ref("changeCrew").addEventListener("click", () => this.openCrewPicker());
    this.ref("spectateJoin").addEventListener("click", () => this.openCrewPicker());
    this.ref("spectateWait").addEventListener("click", () => this.chooseCrew("new"));
    this.ref("crewClose").addEventListener("click", () => this.closeCrewPicker());
    this.ref("newCrew").addEventListener("click", () => this.chooseCrew("new"));
    this.ref("qdAbandon").addEventListener("click", () => handlers.abandon());
    this.ref("leaveBtn").addEventListener("click", () => this.confirm("Leave this room? The other pirates can keep playing.", () => handlers.leave()));
    this.ref("qdClose").addEventListener("click", () => this.closeQuestDialog());
    this.ref("saleClose").addEventListener("click", () => this.closeSale());
    this.ref("confirmYes").addEventListener("click", () => {
      const cb = this.confirmCb;
      this.closeConfirm();
      cb?.();
    });
    this.ref("confirmNo").addEventListener("click", () => this.closeConfirm());
    this.bindSettings();
    this.renderOnboarding();
  }

  ref(id: string): HTMLElement {
    const e = this.r[id];
    if (!e) throw new Error(`hud ref ${id}`);
    return e;
  }

  private bindSettings(): void {
    const s = settings.value;
    const graphics = byId(this.root, "graphicsPreset", HTMLSelectElement);
    graphics.value = s.graphicsPreset;
    graphics.addEventListener("change", () => settings.update({ graphicsPreset: graphicsPreset(graphics.value) }));
    const contacts = byId(this.root, "contactShadowSamples", HTMLSelectElement);
    contacts.value = String(s.contactShadowSamples);
    contacts.addEventListener("change", () => settings.update({ contactShadowSamples: contacts.value === "24" ? 24 : 48 }));
    const range = (id: string, key: "sensitivity" | "master" | "effects" | "ambience"): void => {
      const input = byId(this.root, id, HTMLInputElement);
      input.value = String(s[key]);
      input.addEventListener("input", () => settings.update({ [key]: Number(input.value) }));
    };
    range("sens", "sensitivity");
    range("vMaster", "master");
    range("vFx", "effects");
    range("vAmb", "ambience");
    const mute = byId(this.root, "mute", HTMLInputElement);
    mute.checked = s.muted;
    mute.addEventListener("change", () => settings.update({ muted: mute.checked }));
    const invert = byId(this.root, "invert", HTMLInputElement);
    invert.checked = s.invertY;
    invert.addEventListener("change", () => settings.update({ invertY: invert.checked }));
  }

  private async copyLink(): Promise<void> {
    const params = new URLSearchParams();
    if (this.ownSlot >= 0) params.set("crew", String(this.ownSlot));
    const link = `${location.origin}${location.pathname}?${params}`;
    try {
      await navigator.clipboard.writeText(link);
      this.toast("good", "Invite link copied. Send it to your crew!");
    } catch {
      this.toast("info", `Invite link: ${link}`);
    }
  }

  // ---------------------------------------------------------------------------

  setCrew(list: readonly CrewEntry[]): void {
    const key = list.map((c) => `${c.id}|${c.name}|${c.connected}|${c.dead}|${c.station}|${c.cannonLoaded}|${c.mode}|${c.self}|${c.bot}`).join(";");
    if (key === this.crewKey) return;
    this.crewKey = key;
    const ul = this.ref("crewList");
    ul.replaceChildren(
      ...list.map((c) => {
        const status = !c.connected ? "⟳ Reconnecting…" : c.dead ? "✖ Knocked out" : c.cannonLoaded ? "In a cannon" : c.mode === MODE_LAUNCH ? "In flight" :
          c.station === "wheel" ? "☸ At the wheel" : c.station.startsWith("cannon:") || c.station.startsWith("islandCannon:") ? "At a cannon" : "● Connected";
        return h("li", { class: c.connected ? "ok" : "lost" }, [h("strong", {}, [c.name + (c.bot ? " (bot)" : "") + (c.self ? " (you)" : "")]), " ", h("span", { class: "status" }, [status])]);
      }),
    );
    if (list.length < 2) ul.append(h("li", { class: "hint" }, ["Waiting for a crewmate — share the invite link."]));
  }

  setCrews(list: readonly CrewView[], ownSlot: number, autoPick = true): void {
    this.autoPick = autoPick;
    const changedCrew = this.ownSlot !== ownSlot;
    this.ownSlot = ownSlot;
    const key = `${ownSlot};${list.map((c) => `${c.slot}|${c.name}|${c.color}|${c.members}|${c.humans}|${c.memberNames.join(",")}|${c.gold}`).join(";")}`;
    if (key !== this.crewsKey) {
      this.crewsKey = key;
      const own = list.find((c) => c.slot === ownSlot);
      setText(this.ref("ownCrew"), own ? `Your crew: ${own.name}` : "Choose a crew");
      if (own) {
        setStyle(this.ref("ownCrew"), "color", own.color);
        setStyle(this.ref("ownSwatch"), "background", own.color);
      }
      toggle(this.ref("ownSwatch"), !!own);
      this.ref("crewChoices").replaceChildren(
        ...list.map((c) => {
          const button = h("button", { type: "button" }, ["Join"]);
          if (button instanceof HTMLButtonElement) button.disabled = c.slot === ownSlot || c.humans >= CREW.maxSize;
          button.addEventListener("click", () => {
            if (Number.isInteger(c.slot) && c.slot >= 0 && c.slot < CREW.maxCrews) this.chooseCrew(c.slot);
          });
          return h("div", { class: "crew-choice" }, [
            h("div", {}, [
              h("span", { "aria-hidden": "true", style: `display:inline-block;width:0.8em;height:0.8em;margin-right:0.35em;background:${c.color}` }),
              h("strong", { style: `color:${c.color}` }, [c.name]),
              h("div", { class: "hint" }, [`${c.members}/${CREW.maxSize} pirates · ${c.gold.toLocaleString("en")} gold`]),
              h("div", { class: "hint" }, [c.memberNames.join(", ")]),
            ]),
            button,
          ]);
        }),
      );
      byId(this.root, "newCrew", HTMLButtonElement).disabled = list.length >= CREW.maxCrews;
      const params = new URLSearchParams(location.search);
      if (ownSlot >= 0) params.set("crew", String(ownSlot));
      else params.delete("crew");
      history.replaceState(null, "", `?${params}`);
    }
    toggle(this.ref("crewClose"), ownSlot >= 0 || !autoPick);
    if (ownSlot < 0 && autoPick && this.ref("crewPicker").hidden) this.openCrewPicker();
    else if (changedCrew && ownSlot >= 0) {
      this.closeDialogs();
      this.closeSale();
    }
  }

  openCrewPicker(): void {
    this.setPaused(false, "");
    this.setMapOpen(false);
    toggle(this.ref("crewPicker"), true);
    this.handlers.releaseForDialog();
    (this.ref("crewChoices").querySelector<HTMLButtonElement>("button:not(:disabled)") ?? this.ref("newCrew")).focus();
  }

  closeCrewPicker(): void {
    if (this.ownSlot >= 0 || !this.autoPick) toggle(this.ref("crewPicker"), false);
  }

  private chooseCrew(choice: CrewChoice): void {
    const choose = (): void => this.handlers.chooseCrew(choice);
    if (this.ownSlot >= 0) this.confirm("Switch crews? You'll drop what you're carrying and respawn on the new ship.", choose);
    else choose();
  }

  setSupplies(cannonballs: number, planks: number, reload: number | null, selfLaunch = false): void {
    setText(this.ref("supplies"), `🎯 Cannonballs ${cannonballs} · 🪵 Planks ${planks}`);
    toggle(this.ref("cannonReload"), reload !== null);
    if (reload !== null) setText(this.ref("cannonReload"), reload > 0 ? `Reloading ${Math.ceil(reload)}s` : selfLaunch ? "Ready to launch" : "Cannon ready");
  }

  setGold(gold: number, titleIndex: number): void {
    setText(this.ref("gold"), gold.toLocaleString("en"));
    setText(this.ref("title"), CREW_TITLES[titleIndex]?.title ?? "");
  }

  setObjective(text: string, hint: string | null): void {
    setText(this.ref("objective"), text);
    toggle(this.ref("questHint"), hint !== null);
    if (hint) setText(this.ref("questHint"), hint);
  }

  setQuestMap(island: IslandId | null, spot: number): void {
    const key = island ? `${island}:${spot}:${island === "lantern" ? this.lanternDiscoveries : 0}` : "";
    if (key === this.questKey) return;
    this.questKey = key;
    this.worldMap.setQuest(island);
    const holder = this.ref("mapQuest");
    holder.replaceChildren();
    if (island) {
      const l = getIsland(island);
      setText(this.ref("mapQuestTitle"), `Treasure map: ${l.name}`);
      holder.append(questMapSvg(island, spot, this.lanternDiscoveries));
      setText(this.ref("mapQuestHint"), `Dig at the X. ${l.digSpots[spot]?.hint ?? ""}`);
    } else {
      setText(this.ref("mapQuestTitle"), "No active quest");
      setText(this.ref("mapQuestHint"), "Visit any outpost quest board to choose a treasure quest.");
    }
  }

  setIslandObjective(text: string | null): void {
    toggle(this.ref("islandObjective"), text !== null);
    if (text) setText(this.ref("islandObjective"), `Lantern Isle · ${text}`);
  }

  setLanternJournal(crewSlot: number, discoveries: number, clues: number, solved: boolean, state: LanternState): void {
    const last = this.lanternSnapshot;
    if (last.crewSlot === crewSlot && last.discoveries === discoveries && last.clues === clues &&
      last.solved === solved && last.bearing === state.bearing && last.colour === state.colour &&
      last.revealed === state.revealed && last.cargoTaken === state.cargoTaken) return;
    if (last.crewSlot !== crewSlot || last.discoveries !== discoveries || last.clues !== clues) this.renderLanternJournal(discoveries, clues);
    last.crewSlot = crewSlot;
    last.discoveries = discoveries;
    last.clues = clues;
    last.solved = solved;
    last.bearing = state.bearing;
    last.colour = state.colour;
    last.revealed = state.revealed;
    last.cargoTaken = state.cargoTaken;
    this.lanternDiscoveries = discoveries;
    const lighthouse = LANTERN_SITES.find((site) => site.id === "lighthouse")!;
    const settings = discoveries & lighthouse.bit ? ` Bearing: ${LANTERN_BEARINGS[state.bearing] ?? "—"} · light: ${LANTERN_COLOURS[state.colour] ?? "—"}.` : "";
    setText(this.ref("journalSignal"), state.revealed
      ? `The keeper's safe signal revealed cargo beside the western wreck.${state.cargoTaken ? " The recovered cargo has been claimed; carry physical treasure to an outpost merchant." : " Recover the chest beside the hull."}${settings}`
      : `${solved ? "Your crew restored the safe signal earlier in this voyage. The mechanism can be set again." : "The keeper's signal has not yet been restored."}${settings}`);
  }

  private renderLanternJournal(discoveries: number, clues: number): void {
    const sites = LANTERN_SITES.filter((site) => discoveries & site.bit);
    setText(this.ref("journalSummary"), `${sites.length}/${LANTERN_SITES.length} places discovered · ${[LANTERN_RULES.clueJournal, LANTERN_RULES.clueMarkings, LANTERN_RULES.clueLogbook].filter((bit) => clues & bit).length}/3 notes collected`);
    this.ref("journalChart").replaceChildren(lanternMapSvg(discoveries));
    this.ref("journalSites").replaceChildren(...(sites.length ? sites.map((site) => h("li", {}, [
      h("strong", {}, [site.title]), h("p", {}, [site.description]),
    ])) : [h("li", { class: "hint" }, ["Go ashore on Lantern Isle to begin your crew's record."])]));
    const notes = [
      { id: "journal", bit: LANTERN_RULES.clueJournal },
      { id: "markings", bit: LANTERN_RULES.clueMarkings },
      { id: "logbook", bit: LANTERN_RULES.clueLogbook },
    ] as const;
    const entries: HTMLElement[] = [];
    for (const note of notes) {
      if (!(clues & note.bit)) continue;
      const item = LANTERN_INTERACTIONS.find((interaction) => interaction.id === note.id)!;
      const button = h("button", { type: "button" }, [`Read again: ${item.title}`]);
      button.addEventListener("click", () => this.openIslandNote(note.id, true));
      entries.push(h("article", { class: "journal-clue" }, [
        h("h4", {}, [item.title]), ...this.noteParagraphs(item.text), button,
      ]));
    }
    if (!entries.length) entries.push(h("p", { class: "hint" }, ["No notes collected yet. The keeper's cottage is a good place to look."]));
    this.ref("journalClues").replaceChildren(...entries);
  }

  private noteParagraphs(text: string): HTMLElement[] {
    return text.split(/\n\s*\n/).filter(Boolean).map((paragraph) => h("p", {}, [paragraph]));
  }

  showIslandRead(id: LanternInteractionId): void {
    if (id !== "journal" && id !== "markings" && id !== "logbook") return;
    this.openIslandNote(id, false);
  }

  private showJournalPage(): void {
    this.islandReturnToJournal = false;
    setText(this.ref("islandTitle"), "Crew exploration journal");
    setText(this.ref("islandCaption"), "Lantern Isle · places & collected clues");
    setText(this.ref("islandClose"), this.mapOpen ? "Back to map" : "Close journal & return");
    toggle(this.ref("islandNoteView"), false);
    toggle(this.ref("islandJournalView"), true);
    this.ref("islandScroll").scrollTop = 0;
    this.ref("islandClose").focus();
  }

  private openIslandJournal(): void {
    this.showJournalPage();
    this.openIslandNotes();
  }

  private openIslandNote(id: LanternInteractionId, fromJournal: boolean): void {
    const item = LANTERN_INTERACTIONS.find((interaction) => interaction.id === id);
    if (!item) return;
    this.islandReturnToJournal = fromJournal;
    setText(this.ref("islandTitle"), item.title);
    setText(this.ref("islandCaption"), `Lantern Isle · ${LANTERN_SITES.find((site) => site.id === item.site)?.title ?? "Collected note"}`);
    this.ref("islandNoteText").replaceChildren(...this.noteParagraphs(item.text));
    setText(this.ref("islandClose"), fromJournal ? "Back to journal" : this.mapOpen ? "Back to map" : "Close note & return");
    toggle(this.ref("islandNoteView"), true);
    toggle(this.ref("islandJournalView"), false);
    this.ref("islandScroll").scrollTop = 0;
    this.openIslandNotes();
  }

  private openIslandNotes(): void {
    const dialog = this.islandDialog;
    if (!dialog.open) {
      this.islandFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.setPaused(false, "");
      dialog.showModal();
    }
    this.handlers.releaseForDialog();
    this.ref("islandClose").focus();
  }

  private closeIslandNotes(): void {
    const dialog = this.islandDialog;
    if (!dialog.open) return;
    dialog.close();
    this.islandReturnToJournal = false;
    this.islandFocus?.focus();
    this.islandFocus = null;
  }

  setVitals(health: number, breath: number | null, carrying: boolean): void {
    setStyle(this.ref("health"), "width", `${Math.max(0, health)}%`);
    setText(this.ref("healthNum"), String(Math.ceil(health)));
    this.ref("health").classList.toggle("low", health < 35);
    toggle(this.ref("breathRow"), breath !== null);
    if (breath !== null) {
      setStyle(this.ref("breath"), "width", `${(breath / 20) * 100}%`);
      setText(this.ref("breathNum"), `${Math.ceil(breath)}s`);
      this.ref("breath").classList.toggle("low", breath < 6);
    }
    toggle(this.ref("carry"), carrying);
  }
  setEquipment(equipment: Equipment, blocking: boolean): void {
    this.ref("equipSword").classList.toggle("selected", equipment === "sword");
    this.ref("equipShovel").classList.toggle("selected", equipment === "shovel");
    this.ref("equipSpyglass").classList.toggle("selected", equipment === "spyglass");
    this.ref("equipPistol").classList.toggle("selected", equipment === "pistol");
    this.ref("equipSword").classList.toggle("blocking", blocking);
    setText(this.ref("equipmentHint"), equipment === "sword" ? "Click: Slash / chain · Tap RMB on impact: Parry · Hold: Guard" :
      equipment === "pistol" ? "Click: Fire · Hold RMB: Aim · R: Reload" :
      equipment === "spyglass" ? "Hold RMB: Spyglass · 4x" : "Left click: Dig on land");
  }

  setPistol(visible: boolean, rounds: number, reserve: number, reloadRemaining: number, recoveryRemaining: number): void {
    const ammo = this.ref("pistolAmmo"), status = this.ref("pistolStatus");
    ammo.hidden = status.hidden = !visible;
    setText(ammo, `Cylinder ${rounds}/${PISTOL.capacity} · Reserve ${reserve}`);
    setText(status, reloadRemaining > 0 ? `Reloading ${reloadRemaining.toFixed(1)}s` :
      recoveryRemaining > 0 ? "Recovering" : rounds > 0 ? "Ready" :
      reserve > 0 ? "Press R to reload" : "Out of ammo — restock at home supplies or an island barrel");
  }

  /** Lens mask for the spyglass or a mounted telescope; `magnification` labels the lens. */
  setScope(blend: number, magnification: number): void {
    const opacity = Math.max(0, Math.min(1, blend * 2 - 1));
    const mask = this.ref("scopeMask");
    toggle(mask, opacity > 0);
    if (mask.style.opacity !== String(opacity)) mask.style.opacity = String(opacity);
    setText(this.ref("scopeLabel"), `${magnification}x`);
    this.root.classList.toggle("scoped", blend >= 0.999);
  }

  setLocomotion(state: "" | "sprint" | "crouch" | "slide"): void {
    setText(this.ref("locomotion"), state === "slide" ? "Sliding" : state === "sprint" ? "Sprinting" : state === "crouch" ? "Crouched" : "");
  }

  /** Immediate feedback for scrub-driven presentation; null clears both cues. */
  setMeleeFeedback(result: "hit" | "blocked" | "parried" | "hurt" | null): void {
    if (result === null) {
      toggle(this.ref("hurtVignette"), false);
      toggle(this.ref("meleeMarker"), false);
      return;
    }
    if (result === "hurt") {
      toggle(this.ref("hurtVignette"), true);
      return;
    }
    const marker = this.ref("meleeMarker");
    marker.classList.toggle("blocked", result === "blocked");
    marker.classList.toggle("parried", result === "parried");
    setText(marker, result === "parried" ? "PARRY" : result === "blocked" ? "BLOCK" : "×");
    toggle(marker, true);
  }

  /** Confirmation-only combat feedback; calling again extends the visible cue. */
  showMeleeFeedback(result: "hit" | "blocked" | "parried" | "hurt"): void {
    this.setMeleeFeedback(result);
    if (result === "hurt") {
      clearTimeout(this.hurtTimer);
      this.hurtTimer = window.setTimeout(() => toggle(this.ref("hurtVignette"), false), 200);
      return;
    }
    clearTimeout(this.meleeTimer);
    this.meleeTimer = window.setTimeout(() => toggle(this.ref("meleeMarker"), false), result === "parried" ? 250 : 150);
  }

  /** Battle royale spectators have no ship to report. */
  setShipVisible(visible: boolean): void {
    toggle(this.ref("shipPanel"), visible);
  }
  setShip(ship: ShipView, helm: HelmInfo | null, anchored: boolean, docked: boolean, homeName: string): void {
    let leaks = 0;
    for (let m = ship.leaks; m; m &= m - 1) leaks++;
    const condition = 100 - 25 * leaks;
    setStyle(this.ref("hull"), "width", `${condition}%`);
    setText(this.ref("hullNum"), `${condition}% · ${leaks} leak${leaks === 1 ? "" : "s"}`);
    this.ref("hull").classList.toggle("low", leaks >= 2);
    const flooding = ship.flooding;
    setStyle(this.ref("flood"), "width", `${flooding}%`);
    setText(this.ref("floodNum"), `${Math.round(flooding)}%`);
    this.ref("flood").classList.toggle("low", flooding > 50);
    this.setDamage(ship);
    toggle(this.ref("helm"), helm !== null);
    if (helm) {
      setText(this.ref("speed"), `${(helm.speed * 1.944).toFixed(1)} kn`);
      setText(this.ref("heading"), compass(helm.heading));
      setText(this.ref("sail"), SAIL_NAMES[helm.sail] ?? "");
    }
    setText(this.ref("anchorState"), docked ? `⚓ Docked at ${homeName}` : anchored ? "⚓ Anchor down" : "⛵ Under way");
  }

  /** Rebuilds the damage readout only when the ship's damage (or its drive) changes. */
  private setDamage(ship: ShipView): void {
    let wrecked = 0;
    for (let i = 0; i < ship.cannons.length; i++) if (ship.cannons.at(i)?.wrecked) wrecked |= 1 << i;
    const drive = Math.round(shipDrive(ship) * 100);
    const last = this.damageShown;
    if (last.leaks === ship.leaks && last.breaches === ship.breaches && last.ropes === ship.ropes && last.sailHoles === ship.sailHoles &&
      last.wheelHp === ship.wheelHp && last.wrecked === wrecked && last.drive === drive) return;
    Object.assign(last, { leaks: ship.leaks, breaches: ship.breaches, ropes: ship.ropes, sailHoles: ship.sailHoles, wheelHp: ship.wheelHp, wrecked, drive });
    const items = damageReport(ship);
    toggle(this.ref("damage"), items.length > 0);
    if (!items.length) return;
    setStyle(this.ref("drive"), "width", `${drive}%`);
    setText(this.ref("driveNum"), `${drive}%`);
    this.ref("drive").classList.toggle("low", drive < 50);
    setText(this.ref("damageList"), items.join(" · "));
  }

  setPrompt(text: string | null, progress: number | null): void {
    const p = this.ref("prompt");
    toggle(p, text !== null);
    if (text) setText(this.ref("promptText"), text);
    toggle(this.ref("ring"), progress !== null);
    if (progress !== null) setStyle(this.ref("ring"), "--p", `${Math.round(progress * 100)}`);
  }

  toast(kind: ToastKind, text: string, autoClose = true): void {
    const t = h("div", { class: `toast ${kind}` }, [h("span", { "aria-hidden": "true", class: "icon" }, [TOAST_ICON[kind]]), text]);
    const box = this.ref("toasts");
    box.append(t);
    while (box.children.length > 5) box.firstElementChild?.remove();
    if (autoClose) setTimeout(() => t.remove(), 5000);
  }

  setBanner(id: "boundary" | "vote" | "recover" | "conn" | "royaleRing", text: string | null): void {
    const e = this.ref(id);
    toggle(e, text !== null);
    if (text !== null && id !== "boundary") setText(e, text);
  }

  setPhaseChip(chip: PhaseChip | null): void {
    const el = this.ref("phaseChip");
    toggle(el, chip !== null);
    if (!chip) return;
    setText(this.ref("phaseTag"), chip.tag);
    setText(this.ref("phaseDetail"), chip.detail);
    setText(this.ref("phaseRole"), chip.role);
    if (el.dataset.tone !== chip.tone) el.dataset.tone = chip.tone;
  }

  showPhaseSplash(s: PhaseSplash): void {
    const el = this.ref("phaseSplash");
    setText(this.ref("phaseSplashTitle"), s.title);
    setText(this.ref("phaseSplashText"), s.text);
    el.classList.toggle("big", s.big);
    el.classList.remove("info", "good", "bad");
    el.classList.add(s.tone);
    toggle(el, true);
    clearTimeout(this.splashTimer);
    this.splashTimer = window.setTimeout(() => toggle(this.ref("phaseSplash"), false), s.seconds * 1000);
  }

  setRoyaleResults(r: RoyaleResults | null): void {
    toggle(this.ref("royaleResults"), r !== null);
    if (!r) return;
    setText(this.ref("resultsTitle"), r.title);
    setText(this.ref("resultsReason"), r.reason);
    setText(this.ref("resultsNext"), r.next);
    toggle(this.ref("phaseSplash"), false);
    clearTimeout(this.splashTimer);
  }

  setSpectator(v: SpectatorView | null): void {
    toggle(this.ref("spectateCard"), v !== null);
    if (!v) return;
    setText(this.ref("spectateTarget"), v.target);
    setText(this.ref("spectateStatus"), v.status);
    toggle(this.ref("spectateWait"), v.canWait);
  }

  setDeath(respawnIn: number | null): void {
    toggle(this.ref("death"), respawnIn !== null);
    if (respawnIn !== null) setText(this.ref("deathText"), `Respawning in ${Math.ceil(respawnIn)}…`);
  }

  setSinking(sinkIn: number | null, text: (seconds: number) => string): void {
    toggle(this.ref("sinking"), sinkIn !== null);
    if (sinkIn !== null) setText(this.ref("sinkText"), text(Math.ceil(sinkIn)));
  }

  showSale(msg: SaleMsg, autoClose = true): void {
    setText(this.ref("saleChest"), msg.chest);
    setText(this.ref("saleEarned"), String(msg.earned));
    setText(this.ref("saleTotal"), msg.total.toLocaleString("en"));
    setText(this.ref("saleQuests"), String(msg.quests));
    setText(this.ref("saleTitle"), msg.title);
    toggle(this.ref("saleNew"), msg.newTitle);
    toggle(this.ref("sale"), true);
    clearTimeout(this.saleTimer);
    this.saleTimer = autoClose ? window.setTimeout(() => this.closeSale(), 7000) : 0;
  }

  closeSale(): void {
    toggle(this.ref("sale"), false);
  }

  get mapOpen(): boolean {
    return !this.ref("mapOverlay").hidden;
  }

  setMapOpen(open: boolean): void {
    const wasOpen = this.mapOpen;
    toggle(this.ref("mapOverlay"), open);
    if (open && !wasOpen) {
      this.handlers.releaseForDialog();
      this.ref("mapClose").focus();
    }
  }

  setMapMarkers(markers: readonly MapMarker[]): void {
    this.worldMap.setMarkers(markers);
  }

  setMapRing(radius: number): void {
    this.worldMap.setRing(radius);
  }

  /** Screen-space treasure marker (CSS px); `arrow` is the off-screen bearing (0 = up), null when the treasure is in view. */
  setTreasurePointer(p: { x: number; y: number; arrow: number | null; label: string } | null): void {
    const el = this.ref("treasurePointer");
    toggle(el, p !== null);
    if (!p) return;
    el.style.transform = `translate(${p.x.toFixed(0)}px, ${p.y.toFixed(0)}px) translate(-50%, -50%)`;
    const arrow = this.ref("treasureArrow");
    toggle(arrow, p.arrow !== null);
    if (p.arrow !== null) arrow.style.transform = `rotate(${p.arrow.toFixed(2)}rad)`;
    setText(this.ref("treasureLabel"), p.label);
  }

  setPopulation(status: SeaStatus): void {
    const total = `${status.population} / ${status.capacity} sailors`;
    const breakdown = `${status.humans} humans · ${status.crewBots} crew bots · ${status.npcSailors} enemy sailors · ${status.reserved} reconnecting`;
    setText(this.ref("population"), total);
    setText(this.ref("populationBreakdown"), breakdown);
    setText(this.ref("onlinePopulation"), total);
    setText(this.ref("onlineBreakdown"), breakdown);
  }

  setOnline(view: OnlineView): void {
    this.setPopulation(view.population);
    const key = JSON.stringify([view.crews, view.players]);
    if (key === this.onlineKey) return;
    this.onlineKey = key;
    this.ref("leaderboard").replaceChildren(...(view.crews.length
      ? view.crews.map((c) => h("li", {}, [h("strong", { style: `color:${c.color}` }, [c.name]), ` · ${c.gold.toLocaleString("en")} gold · ${c.quests} quests`]))
      : [h("li", { class: "hint" }, ["No crews at sea."])]));
    this.ref("onlineList").replaceChildren(...view.players.map((p) => h("li", {}, [
      h("strong", {}, [p.name + (p.bot ? " (bot)" : "")]),
      ` · ${p.crewName || "Crewless"}${p.connected ? "" : " · Reconnecting…"}`,
    ])));
  }

  get onlineOpen(): boolean { return !this.ref("online").hidden; }

  toggleOnline(): void {
    if (this.onlineOpen) { this.closeOnline(); return; }
    if (this.dialogOpen) return;
    this.dialogFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.setPaused(false, "");
    this.setMapOpen(false);
    toggle(this.ref("online"), true);
    this.handlers.releaseForDialog();
    this.ref("onlineClose").focus();
  }

  closeOnline(): void {
    if (!this.onlineOpen) return;
    toggle(this.ref("online"), false);
    this.dialogFocus?.focus();
    this.dialogFocus = null;
  }

  openBoard(): void {
    this.dialogFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.setPaused(false, "");
    this.setMapOpen(false);
    toggle(this.ref("board"), true);
    this.handlers.releaseForDialog();
    this.ref("takeMap").focus();
  }

  closeBoard(): void {
    if (this.ref("board").hidden) return;
    toggle(this.ref("board"), false);
    this.dialogFocus?.focus();
    this.dialogFocus = null;
  }

  get dialogOpen(): boolean {
    return this.changelog.isOpen || this.islandDialog.open || this.onlineOpen || !this.ref("board").hidden || !this.ref("questDialog").hidden || !this.ref("confirm").hidden || !this.ref("crewPicker").hidden;
  }

  openQuestDialog(island: IslandId, spot: number, voteBlocked: string | null): void {
    const l = getIsland(island);
    setText(this.ref("qdTitle"), `Current quest: ${l.name}`);
    this.ref("qdMap").replaceChildren(questMapSvg(island, spot, this.lanternDiscoveries));
    setText(this.ref("qdHint"), voteBlocked ?? l.digSpots[spot]?.hint ?? "");
    toggle(this.ref("questDialog"), true);
    this.handlers.releaseForDialog();
    this.ref("qdClose").focus();
  }

  closeQuestDialog(): void {
    toggle(this.ref("questDialog"), false);
  }

  confirm(text: string, cb: () => void): void {
    this.confirmCb = cb;
    setText(this.ref("confirmText"), text);
    toggle(this.ref("confirm"), true);
    this.handlers.releaseForDialog();
    this.ref("confirmNo").focus();
  }

  closeConfirm(): void {
    this.confirmCb = null;
    toggle(this.ref("confirm"), false);
  }

  closeDialogs(): void {
    this.closeQuestDialog();
    this.closeIslandNotes();
    this.closeConfirm();
    this.closeCrewPicker();
    this.closeOnline();
    this.closeBoard();
    this.changelog.close();
  }

  setPaused(open: boolean, note: string): void {
    toggle(this.ref("pause"), open);
    setText(this.ref("pauseNote"), note);
    if (open) byId(this.root, "graphicsPreset", HTMLSelectElement).value = settings.value.graphicsPreset;
    if (open) byId(this.root, "contactShadowSamples", HTMLSelectElement).value = String(settings.value.contactShadowSamples);
    if (open) this.ref("resume").focus();
  }

  get paused(): boolean {
    return !this.ref("pause").hidden;
  }

  setMenuState(canRecover: boolean, hasQuest: boolean): void {
    byId(this.root, "recoverBtn", HTMLButtonElement).disabled = !canRecover;
    byId(this.root, "abandonBtn", HTMLButtonElement).disabled = !hasQuest;
  }

  setOnboardingVisible(v: boolean): void {
    this.onboardingVisible = v;
    settings.update({ onboardingHidden: !v });
    this.renderOnboarding();
  }

  renderOnboarding(): void {
    const done = settings.value.onboarding;
    const all = ONBOARDING_STEPS.every((s) => done.includes(s));
    toggle(this.ref("onboarding"), this.onboardingVisible && !all);
    this.ref("onboardingList").replaceChildren(...ONBOARDING_STEPS.map((s) => h("li", { class: done.includes(s) ? "done" : "" }, [`${done.includes(s) ? "☑" : "☐"} ${ONBOARDING_TEXT[s]}`])));
  }

  dispose(): void {
    clearTimeout(this.saleTimer);
    clearTimeout(this.meleeTimer);
    clearTimeout(this.splashTimer);
    clearTimeout(this.hurtTimer);
    this.changelog.dispose();
    this.root.remove();
  }
}
